import { useState, useCallback, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { Logo } from '@/shared/components/logo'
import { NicolAIAvatar, LocalizedLink } from '@/shared/components'
import { ChatPanel } from './chat-panel'
import { ChatDrawer } from './chat-drawer'

interface PrecadrageLayoutProps {
  children: ReactNode
  onInjectField?: (field: string, content: string) => void
}

export function PrecadrageLayout({ children, onInjectField }: PrecadrageLayoutProps) {
  const { t, i18n } = useTranslation()
  const [isChatOpen, setIsChatOpen] = useState(false)

  const handleInjectField = useCallback(
    (field: string, content: string) => {
      onInjectField?.(field, content)
    },
    [onInjectField]
  )

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en'
    i18n.changeLanguage(newLang)
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-6 lg:px-8">
        <div className="flex items-center gap-4">
          {/* Back button - always visible */}
          <LocalizedLink
            to="/dashboard"
            className="flex items-center gap-2 text-sm text-foreground-muted transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.back')}</span>
          </LocalizedLink>

          {/* Separator */}
          <div className="h-6 w-px bg-border" />

          {/* Logo with frame - links to dashboard */}
          <LocalizedLink to="/dashboard" className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Logo className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-foreground">
              <span className="hidden lg:inline">{t('precadrage.header.title')}</span>
              <span className="lg:hidden">{t('precadrage.header.mobileTitle')}</span>
            </span>
          </LocalizedLink>
        </div>

        <div className="flex items-center gap-4">
          {/* Language switcher - desktop */}
          <button
            type="button"
            onClick={toggleLanguage}
            className="hidden text-sm text-foreground-muted hover:text-foreground lg:block"
          >
            {i18n.language === 'en' ? 'EN | FR' : 'FR | EN'}
          </button>

          {/* Chat button - mobile */}
          <button
            type="button"
            onClick={() => setIsChatOpen(true)}
            className="lg:hidden"
            aria-label={t('precadrage.assistant.title')}
          >
            <NicolAIAvatar size="lg" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className="flex-1 overflow-y-auto bg-background">{children}</div>

        {/* Chat panel - desktop */}
        <div className="hidden w-[420px] shrink-0 border-l border-border lg:block">
          <div className="flex h-full flex-col">
            {/* Chat header */}
            <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background px-5">
              <NicolAIAvatar size="lg" />
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold text-foreground">NicolAI</span>
                <span className="text-xs text-foreground-muted">
                  {t('precadrage.assistant.subtitle')}
                </span>
              </div>
            </div>

            {/* Chat content */}
            <ChatPanel onInjectField={handleInjectField} variant="desktop" />
          </div>
        </div>
      </div>

      {/* Chat drawer - mobile */}
      <ChatDrawer isOpen={isChatOpen} onClose={() => setIsChatOpen(false)}>
        <ChatPanel onInjectField={handleInjectField} variant="mobile" />
      </ChatDrawer>
    </div>
  )
}
